/**
 * TRUS-709: the composer's collision signal — "Maria replied 2 min ago" when
 * another staff member replied in this member conversation after you opened
 * it, with "Show" to scroll to that reply. (No typing indicator this phase.)
 *
 * Sources, merged: GET /api/messaging/channels/{id}/activity?since=<opened>
 * (`other_staff_replies_since`), and the thread's own polled messages — so it
 * also works on a backend without the activity endpoint.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessagesSquare, X } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { Message } from '@/services/messagingApi';
import { memberInboxApi } from '@/services/memberInboxApi';
import {
  mergeOtherReplies,
  otherStaffRepliesFromMessages,
  parseServerTime,
  relativeAgo,
  type OtherReply,
} from './memberInbox';

interface StaffReplyBannerProps {
  channelId: number;
  messages: Message[];
  myUserId: number | null;
  nameOf?: (userId: number) => string | undefined;
  /** Scroll to a message (null: the newest one). */
  onShow: (messageId: number | null) => void;
}

export function StaffReplyBanner({ channelId, messages, myUserId, nameOf, onShow }: StaffReplyBannerProps) {
  const { t } = useAppTranslation();
  // When this viewer opened the conversation; reset on every channel switch.
  const openedAt = useRef<{ channelId: number; at: number }>({ channelId, at: Date.now() });
  if (openedAt.current.channelId !== channelId) openedAt.current = { channelId, at: Date.now() };
  const since = openedAt.current.at;
  const [dismissedAt, setDismissedAt] = useState<number>(0);
  const [, setTick] = useState(0);
  useEffect(() => setDismissedAt(0), [channelId]);
  // Keep "2 min ago" current.
  useEffect(() => {
    const h = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(h);
  }, []);

  const [activityMissing, setActivityMissing] = useState(false);
  const { data: serverReplies } = useQuery({
    queryKey: ['messaging-channel-activity', channelId, since],
    queryFn: async () => {
      const res = await memberInboxApi.getActivity(channelId, new Date(since).toISOString());
      if (res === null) setActivityMissing(true);
      return res ?? [];
    },
    enabled: !activityMissing,
    refetchInterval: 15_000,
    retry: false,
  });

  const replies: OtherReply[] = useMemo(() => {
    const local = otherStaffRepliesFromMessages(messages, since, myUserId, nameOf);
    return mergeOtherReplies(serverReplies, local, since, myUserId).filter(
      (r) => Number.isNaN(parseServerTime(r.at)) || parseServerTime(r.at) > dismissedAt,
    );
  }, [messages, serverReplies, since, myUserId, nameOf, dismissedAt]);

  if (replies.length === 0) return null;
  const newest = replies[0];
  const name = newest.name || t('messaging.sender.staff', 'Staff');
  const others = replies.length - 1;

  const show = () => {
    let id = newest.message_id;
    if (id == null) {
      // The activity endpoint names no message: the newest one from that person.
      const fromThem = messages.filter((m) => m.sender?.user_id === newest.user_id);
      id = fromThem.length ? fromThem[fromThem.length - 1].id : null;
    }
    onShow(id);
  };

  return (
    <div
      className="flex items-center gap-2 border-t border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 sm:px-4"
      role="status"
      data-testid="staff-reply-banner"
    >
      <MessagesSquare className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">
        {others > 0
          ? t('messaging.collision.repliedWithOthers', '{{name}} replied {{ago}} (and {{count}} more)', {
              name,
              ago: relativeAgo(newest.at, t),
              count: others,
            })
          : t('messaging.collision.replied', '{{name}} replied {{ago}}', { name, ago: relativeAgo(newest.at, t) })}
      </span>
      <button
        type="button"
        className="font-semibold underline underline-offset-2 hover:no-underline"
        onClick={show}
        data-testid="staff-reply-banner-show"
      >
        {t('messaging.collision.show', 'Show')}
      </button>
      <button
        type="button"
        className="rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900"
        onClick={() => setDismissedAt(Date.now())}
        aria-label={t('messaging.collision.dismiss', 'Dismiss')}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export default StaffReplyBanner;
